import { pushDataToDataLayer } from '../Global/analytics'

class AnalyticsEvent extends CustomEvent<any> {
  constructor(detail: any) {
    super('aaAnalyticWrapperEvent', { detail, bubbles: true, composed: true })
  }
}

class AnalyticsManager extends HTMLElement {
  private enabledAnalytics: boolean = false
  private enabledLogging: boolean = false
  private disableAscendEvents: boolean = false
  private boundHandleAnalyticEvent: EventListener
  private static activeInstance: AnalyticsManager | null = null
  private static instanceCount: number = 0

  constructor() {
    super()

    // check if multiple analytics managers exist
    AnalyticsManager.activeInstance = AnalyticsManager.activeInstance || this

    // Bind the event handler once to avoid binding issues
    this.boundHandleAnalyticEvent = this.handleAnalyticEvent.bind(this) as EventListener

    this.attachShadow({ mode: 'open' })
    if (this.shadowRoot) {
      this.shadowRoot.innerHTML = `<!-- Analytics Manager -->`
    }
  }

  connectedCallback() {
    AnalyticsManager.instanceCount++
    console.log('AnalyticsManager connected')
    this.addListener()

    if (this.isActiveInstance()) {
      document.AscendAwesome = document.AscendAwesome || {
        version: 'lts',
        theme: 'default',
        components: new Map<string, HTMLElement>(),
      }
      document.AscendAwesome.AnalyticsManager = {
        fireEvent: this.fireEvent.bind(this),
        fireJourney: this.handleJourneyEvent.bind(this),
      }
    }
  }

  disconnectedCallback() {
    AnalyticsManager.instanceCount--
    // Clean up event listener when component is removed
    this.removeListener()

    // If this was the active instance, clear it
    if (this.isActiveInstance()) {
      AnalyticsManager.activeInstance = null
    }
  }

  fireEvent(gtmModel: any, journeyModel?: any) {
    this.handleAnalyticEvent(new AnalyticsEvent(gtmModel))
    if (journeyModel) {
      this.handleJourneyEvent(journeyModel)
    }
  }

  private isActiveInstance(): boolean {
    return AnalyticsManager.instanceCount === 1 && AnalyticsManager.activeInstance === this
  }

  private addListener() {
    if (this.isActiveInstance() && this.enabledAnalytics) {
      document.addEventListener('aaAnalyticEvent', this.boundHandleAnalyticEvent, true)
    }
  }

  private removeListener() {
    if (this.isActiveInstance()) {
      document.removeEventListener('aaAnalyticEvent', this.boundHandleAnalyticEvent.bind(this))
    }
  }

  private handleAnalyticEvent(event: Event) {
    if (!this.isActiveInstance() || !this.enabledAnalytics) {
      return
    }

    const customEvent = event as CustomEvent
    // Process the analytics event data
    const eventData = customEvent.detail ?? {}

    // log and send to data layer
    if (this.enabledLogging) {
      console.log('Tracking event:', eventData)
    }

    if (event?.type === 'aaAnalyticWrapperEvent') {
      eventData.ascendType = 'eventApp'
      pushDataToDataLayer(eventData)
      return
    }

    if (this.disableAscendEvents) {
      return
    }

    eventData.ascendType = 'eventComponent'
    pushDataToDataLayer(eventData)
  }

  private handleJourneyEvent(journeyModel: any) {
    console.log('Journey event:', journeyModel)
  }

  static get observedAttributes() {
    return ['enable-analytics', 'enable-logging', 'disable-ascend-events']
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    switch (name) {
      case 'enable-analytics':
        this.enabledAnalytics = newValue === 'true'

        if (this.isActiveInstance() && this.enabledAnalytics) {
          this.addListener()
        } else {
          this.removeListener()
        }

        console.log(`Analytics Manager ${this.enabledAnalytics ? 'enabled' : 'disabled'}`)
        break
      case 'enable-logging':
        this.enabledLogging = newValue === 'true'
        console.log(`Analytics Manager logging ${this.enabledLogging ? 'enabled' : 'disabled'}`)
        break
      case 'disable-ascend-events':
        this.disableAscendEvents = newValue === 'true'
        console.log(
          `Analytics Manager Ascend events ${this.disableAscendEvents ? 'disabled' : 'enabled'}`,
        )
        break
    }
  }
}

if (!customElements.get('ts-analytics-manager')) {
  customElements.define('ts-analytics-manager', AnalyticsManager)
}
export { AnalyticsManager }
